"use client";

import { useEffect, useState } from "react";

/** sessionStorage key that remembers "this tab is the app's webview". */
export const MOBILE_WEBVIEW_SESSION_KEY = "cheervueMobileWebView";

/**
 * True when the page is rendering inside the CheerVue mobile app's webview.
 *
 * Three signals, any of which is enough — the same "accept either signal" shape
 * `hasWalletHandoffCapability` uses, plus a sticky marker:
 *
 * 1. `window.__isCheerVueMobile` — injected by the sheet on every page it loads.
 *    Nothing outside the app can set it, so this is the signal that writes the
 *    sticky marker below. The injection runs at document END
 *    (`WKUserScriptInjectionTimeAtDocumentEnd`, RNCWebViewImpl.m:1691) — after
 *    the document parses, BEFORE subresources and before the `load` event — so
 *    the flag is normally already there when this hook first reads it.
 * 2. A sessionStorage marker, written the first time the injected flag is seen.
 *    In-page links drop the query string, so later catalog pages rely on this.
 * 3. `?mobile=true` — the app appends it to every webview URL it builds
 *    (`buildWebViewDashboardUrl` in the mobile app). Current page only, and it
 *    never writes the marker: a webview URL that gets copied and shared still
 *    carries the param, and a parent opening that link in a real browser should
 *    get their account buttons back on the very next page.
 *
 * Returns false on the server and on the first client render, then flips after
 * mount: the server has no query string, so answering during render would
 * mismatch hydration. Plain web is unaffected — none of the three signals exist
 * there.
 *
 * This is a presentation hint, not a security boundary. Anyone can type
 * `?mobile=true` into a browser. Use it to decide what to SHOW, never what to
 * allow.
 */
export function useIsMobileWebView(): boolean {
  const [isMobileWebView, setIsMobileWebView] = useState(false);

  useEffect(() => {
    let cancelled = false;

    /** The injected flag, and the marker it leaves behind for later pages. */
    const readAppSignal = (): boolean => {
      if ((window as { __isCheerVueMobile?: unknown }).__isCheerVueMobile === true) {
        try {
          window.sessionStorage.setItem(MOBILE_WEBVIEW_SESSION_KEY, "1");
        } catch {
          // Storage can be unavailable (private mode, blocked cookies). The
          // injected flag still answers for the current page.
        }
        return true;
      }
      try {
        return window.sessionStorage.getItem(MOBILE_WEBVIEW_SESSION_KEY) === "1";
      } catch {
        return false;
      }
    };

    const readQueryParam = (): boolean => {
      try {
        const params = new URLSearchParams(window.location.search);
        return params.get("mobile") === "true" || params.get("isMobile") === "true";
      } catch {
        return false;
      }
    };

    if (readAppSignal() || readQueryParam()) {
      setIsMobileWebView(true);
    }

    // Keep watching even when the query param already answered: the flag is what
    // writes the marker, and the NEXT page in the webview may arrive with no
    // query string at all.
    //
    // The read above is the normal path — injection at document end usually beats
    // hydration. These two cover the rest, and neither is optional:
    //  - the short re-checks catch hydration landing a hair before injection;
    //  - the `load` listener is the guaranteed-late backstop, since `load` always
    //    follows document end.
    // Do NOT delete the timers in favour of the listener alone: `load` waits for
    // every subresource, so on a heavy page that gap is seconds — seconds of an
    // in-app parent looking at the web account menu.
    const recheck = () => {
      if (!cancelled && readAppSignal()) setIsMobileWebView(true);
    };

    window.addEventListener("load", recheck);
    const timers = [0, 300].map((delay) => window.setTimeout(recheck, delay));

    return () => {
      cancelled = true;
      window.removeEventListener("load", recheck);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return isMobileWebView;
}
